// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  BankUploadStep,
  BankUploadStepMeta,
} from '@client/banks/types/bank-upload';
import { createStepsOptions } from '@shared/ui-kit/hooks/use-steps';
import { AccountsFormStep } from '../accounts-form/accounts-form';
import { AccountsImport } from '../accounts-import/accounts-import';
import { AccountsListStep } from '../accounts-list/accounts-list';
import { ConnectExternal } from '../connect-external/connect-external';
import { DebtsFormStep } from '../debts-form/debts-form';
import { DebtsImport } from '../debts-import/debts-import';
import { DebtsListStep } from '../debts-list/debts-list';
import { FlowSelect } from '../flow-select/flow-select';
import { InstitutionManual } from '../institution-manual';
import { InstitutionSelectExisting } from '../institution-select-existing/institution-select-existing';
import { PositionsFormStep } from '../positions-form/positions-form';
import { PositionsImport } from '../positions-import/positions-import';
import { PositionsListStep } from '../positions-list/positions-list';
import { ReviewSummary } from '../review-summary/review-summary';
import { TransactionsFormStep } from '../transactions-form/transactions-form';
import { TransactionsImport } from '../transactions-import/transactions-import';
import { TransactionsListStep } from '../transactions-list/transactions-list';

/**
 * Wizard step graph. Each screen is its own step id; `meta.section` groups steps
 * for drawer chrome tabs / header back. `meta.requires` gates forward navigation;
 * `meta.completed` is set via `upsertMeta` when a section is finished.
 */
export const BankUploadStepsOptions = createStepsOptions<
  BankUploadStepMeta,
  BankUploadStep
>({
  definitions: [
    {
      id: 'flow-select',
      element: <FlowSelect />,
      meta: { section: 'flow' },
    },
    {
      id: 'connect-external',
      element: <ConnectExternal />,
      meta: { section: 'connect' },
    },
    {
      id: 'institution-manual',
      element: <InstitutionManual />,
      meta: { section: 'institution' },
    },
    {
      id: 'institution-select-existing',
      element: <InstitutionSelectExisting />,
      meta: { section: 'institution' },
    },
    {
      id: 'accounts-list',
      element: <AccountsListStep />,
      meta: { section: 'accounts', requires: ['institution'] },
    },
    {
      id: 'accounts-form',
      element: <AccountsFormStep />,
      meta: { section: 'accounts', requires: ['institution'] },
    },
    {
      id: 'accounts-import',
      element: <AccountsImport />,
      meta: { section: 'accounts', requires: ['institution'] },
    },
    {
      id: 'positions-list',
      element: <PositionsListStep />,
      meta: {
        section: 'positions',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'positions-form',
      element: <PositionsFormStep />,
      meta: {
        section: 'positions',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'positions-import',
      element: <PositionsImport />,
      meta: {
        section: 'positions',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'debts-list',
      element: <DebtsListStep />,
      meta: {
        section: 'debts',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'debts-form',
      element: <DebtsFormStep />,
      meta: {
        section: 'debts',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'debts-import',
      element: <DebtsImport />,
      meta: {
        section: 'debts',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'transactions-list',
      element: <TransactionsListStep />,
      meta: {
        section: 'transactions',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'transactions-form',
      element: <TransactionsFormStep />,
      meta: {
        section: 'transactions',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'transactions-import',
      element: <TransactionsImport />,
      meta: {
        section: 'transactions',
        optional: true,
        requires: ['institution'],
      },
    },
    {
      id: 'review-summary',
      element: <ReviewSummary />,
      meta: { section: 'review', requires: ['institution'] },
    },
  ],
  defaultStep: 'flow-select',
});
